import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { Modal } from "@/components/ui/Modal";
import { Pagination } from "@/components/ui/Pagination";
import { EmptyState } from "@/components/ui/EmptyState";
import { ImageUploader } from "@/components/ui/ImageUploader";
import { RatingBadge } from "@/components/ui/RatingBadge";
import { ConditionBadge } from "@/components/listings/ConditionBadge";
import { LocationAutocomplete } from "@/components/ui/LocationAutocomplete";
import { SearchBar } from "@/components/search/SearchBar";

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-gray-200 pb-8">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      {children}
    </div>
  );
}

export function AdminComponentsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [demoPagination, setDemoPagination] = useState(3);
  const [demoImages, setDemoImages] = useState<File[]>([]);
  const [locationValue, setLocationValue] = useState("");
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [demoInput, setDemoInput] = useState("");
  const [demoTextarea, setDemoTextarea] = useState("");
  const [demoSelect, setDemoSelect] = useState("");

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/admin" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        ← Admin valdymo skydas
      </Link>
      <h1 className="text-2xl font-bold text-gray-900">UI komponentai</h1>
      <p className="mt-1 text-sm text-gray-500">
        Egzistuojančių komponentų biblioteka ir demo
      </p>

      <div className="mt-8 space-y-8">
        {/* Badge */}
        <Section title="Badge">
          <div className="flex flex-wrap gap-2">
            <Badge variant="default">Default</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="danger">Danger</Badge>
            <Badge variant="info">Info</Badge>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<Badge variant=\"success\">Tekstas</Badge>"}
          </p>
        </Section>

        {/* RatingBadge */}
        <Section title="RatingBadge">
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-center">
              <RatingBadge rating={8.5} source="bgg" />
              <p className="mt-1 text-xs text-gray-500">BGG</p>
            </div>
            <div className="text-center">
              <RatingBadge rating={7.2} source="local" />
              <p className="mt-1 text-xs text-gray-500">Vietinis (LT)</p>
            </div>
            <div className="text-center">
              <RatingBadge rating={6.8} source="ai" />
              <p className="mt-1 text-xs text-gray-500">AI</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<RatingBadge rating={8.5} source=\"bgg\" />"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Rodo žaidimo reitingą su šaltinio žyme (BGG, LT, AI).
          </p>
        </Section>

        {/* ConditionBadge */}
        <Section title="ConditionBadge">
          <div className="flex flex-wrap items-center gap-3">
            <div className="text-center">
              <ConditionBadge condition="new" />
              <p className="mt-1 text-xs text-gray-500">new</p>
            </div>
            <div className="text-center">
              <ConditionBadge condition="like_new" />
              <p className="mt-1 text-xs text-gray-500">like_new</p>
            </div>
            <div className="text-center">
              <ConditionBadge condition="good" />
              <p className="mt-1 text-xs text-gray-500">good</p>
            </div>
            <div className="text-center">
              <ConditionBadge condition="acceptable" />
              <p className="mt-1 text-xs text-gray-500">acceptable</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<ConditionBadge condition=\"like_new\" />"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Spalvotas ženkliukas skelbimo būklei. Tekstą ima iš vertimų
            (conditions.*). Galimos reikšmės: new, like_new, good, acceptable.
          </p>
        </Section>

        {/* LoadingSpinner */}
        <Section title="LoadingSpinner">
          <div className="flex items-center gap-6">
            <div>
              <LoadingSpinner className="h-5 w-5" />
              <p className="mt-1 text-xs text-gray-500">Mažas</p>
            </div>
            <div>
              <LoadingSpinner />
              <p className="mt-1 text-xs text-gray-500">Normalus</p>
            </div>
            <div>
              <LoadingSpinner className="h-12 w-12" />
              <p className="mt-1 text-xs text-gray-500">Didelis</p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<LoadingSpinner className=\"h-8 w-8\" />"}
          </p>
        </Section>

        {/* Modal */}
        <Section title="Modal">
          <button
            onClick={() => setModalOpen(true)}
            className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-900"
          >
            Atidaryti modalą
          </button>
          <Modal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Demo modalas"
          >
            <p className="text-sm text-gray-600">
              Tai yra modalo turinio pavyzdys. Spauskite Escape arba foną, kad
              uždarytumėte.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setModalOpen(false)}
                className="rounded-lg bg-primary-800 px-4 py-2 text-sm font-medium text-white hover:bg-primary-900"
              >
                Uždaryti
              </button>
            </div>
          </Modal>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<Modal open={bool} onClose={fn} title=\"...\">...</Modal>"}
          </p>
        </Section>

        {/* Pagination */}
        <Section title="Pagination">
          <Pagination
            currentPage={demoPagination}
            totalPages={10}
            onPageChange={setDemoPagination}
          />
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas:{" "}
            {"<Pagination currentPage={n} totalPages={n} onPageChange={fn} />"}
          </p>
        </Section>

        {/* EmptyState */}
        <Section title="EmptyState">
          <div className="rounded-xl border border-gray-200">
            <EmptyState
              title="Nėra rezultatų"
              description="Bandykite pakeisti paieškos kriterijus"
              icon={
                <svg
                  className="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              }
            />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas:{" "}
            {"<EmptyState title=\"...\" description=\"...\" icon={...} />"}
          </p>
        </Section>

        {/* ImageUploader */}
        <Section title="ImageUploader">
          <div className="max-w-md">
            <ImageUploader
              images={demoImages}
              onChange={setDemoImages}
              maxImages={3}
            />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas:{" "}
            {"<ImageUploader images={files} onChange={fn} maxImages={3} />"}
          </p>
        </Section>

        {/* LocationAutocomplete */}
        <Section title="LocationAutocomplete">
          <div className="max-w-md">
            <LocationAutocomplete
              value={locationValue}
              latitude={locationLat}
              longitude={locationLng}
              onChange={(data) => {
                setLocationValue(data.city);
                setLocationLat(data.latitude);
                setLocationLng(data.longitude);
              }}
              label="Miestas"
              placeholder="pvz. Vilnius"
              showMap={false}
            />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas:{" "}
            {
              "<LocationAutocomplete value={str} latitude={n} longitude={n} onChange={fn} />"
            }
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Adreso įvestis su Nominatim autouzpildymu ir pasirinktiniu Leaflet
            žemėlapiu. Naudoja showMap={"{false}"} norint paslėpti žemėlapį.
            GPS mygtukas leidžia nustatyti vietą automatiškai.
          </p>
        </Section>

        {/* SearchBar */}
        <Section title="SearchBar">
          <div className="max-w-md">
            <SearchBar />
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<SearchBar onSelect={(gameId) => ...} />"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Žaidimų paieška su autouzpildymu. Kviečia GraphQL searchGames
            užklausą. Palaiko klaviatūros navigaciją (rodyklės, Enter, Escape).
            Be onSelect nukreipia į žaidimo puslapį.
          </p>
        </Section>

        {/* Form Elements */}
        <Section title="Formos elementai">
          <div className="max-w-md space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Tekstinis laukas (input)
              </label>
              <input
                type="text"
                className="input"
                placeholder="Įveskite tekstą..."
                value={demoInput}
                onChange={(e) => setDemoInput(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Išjungtas laukas (disabled)
              </label>
              <input
                type="text"
                className="input disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="Negalima redaguoti"
                disabled
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Teksto sritis (textarea)
              </label>
              <textarea
                className="input min-h-[100px] resize-y"
                placeholder="Įveskite ilgesnį tekstą..."
                value={demoTextarea}
                onChange={(e) => setDemoTextarea(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Pasirinkimas (select)
              </label>
              <select
                className="input"
                value={demoSelect}
                onChange={(e) => setDemoSelect(e.target.value)}
              >
                <option value="">Pasirinkite...</option>
                <option value="option1">Pirmas variantas</option>
                <option value="option2">Antras variantas</option>
                <option value="option3">Trečias variantas</option>
              </select>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<input className=\"input\" />"} |{" "}
            {"<textarea className=\"input\" />"} |{" "}
            {"<select className=\"input\" />"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            Visi formos elementai naudoja .input CSS klasę, apibrėžtą
            styles/index.css. Ji suteikia vienodą apvalinimą, rėmelį, šešėlį ir
            focus būseną.
          </p>
        </Section>

        {/* Card Styles */}
        <Section title="Kortelių stiliai (card)">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-900">
                Paprasta kortelė
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Bazinė .card klasė su padding.
              </p>
            </div>
            <div className="card overflow-hidden">
              <div className="h-28 bg-gradient-to-br from-primary-100 to-primary-200" />
              <div className="p-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Su paveikslėliu
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Kortelė su viršutiniu vaizdu.
                </p>
              </div>
            </div>
            <div className="card p-5 transition-shadow hover:shadow-md">
              <h3 className="text-sm font-semibold text-gray-900">
                Su hover efektu
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Pridėkite hover:shadow-md interaktyvumui.
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<div className=\"card p-5\">...</div>"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            .card klasė suteikia rounded-xl, border, bg-white ir shadow-sm.
            Padding pridėkite atskirai (p-4, p-5, p-6). Naudokite overflow-hidden
            kai kortelė turi paveikslėlį viršuje.
          </p>
        </Section>

        {/* Button Styles */}
        <Section title="Mygtukai (btn-*)">
          <div className="flex flex-wrap items-center gap-3">
            <button className="btn-primary">Pagrindinis</button>
            <button className="btn-secondary">Antrinis</button>
            <button className="btn-ghost">Ghost</button>
            <button className="btn-primary" disabled>
              Išjungtas
            </button>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Naudojimas: {"<button className=\"btn-primary\">...</button>"}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            CSS klasės: .btn-primary, .btn-secondary, .btn-ghost. Visos
            palaiko disabled būseną automatiškai.
          </p>
        </Section>
      </div>
    </div>
  );
}
